import { CodeableConcept, Coding, Observation } from "@medplum/fhirtypes";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { laborObservationDisplayValuesDiffer } from "@/encounters/components/EncounterLabor/utils/labor-observation-amend-diff-display";
import { getLaborObservationDisplayValuesForVersionDiff } from "@/encounters/components/EncounterLabor/utils/labor-observation-version-diff-values";
import { longUSDateTimeFormat } from "@/utils/datetime";
import { getCodeableConceptCodingLabels, getCodingDisplayLabel } from "@/utils/medplum";

import {
  LaborMedTherapyManeuverRow,
  LaborMedTherapyMedicationRow,
  LaborMedTherapyTherapyRow,
  LaborObservationMedTherapySections,
} from "./LaborObservationMedTherapyDiffColumns";
import {
  getStructuredMedTherapyConfig,
  normalizeObservationFormCode,
} from "./structuredMedTherapyDiffConfig";

interface LaborObservationDiffProps {
  title: string;
  currentObservation?: Observation;
  previousObservation?: Observation;
}

const LaborObservationDiff = ({
  title,
  currentObservation,
  previousObservation,
}: LaborObservationDiffProps) => {
  if (!currentObservation && !previousObservation) return null;

  // Map observation codes to questionnaire types for display names
  const getQuestionnaireTypeFromCode = (
    code: string | undefined,
  ): QuestionnaireType | undefined => {
    const codeMap: Record<string, QuestionnaireType> = {
      fetal_heart_rate: QuestionnaireType.FetalHeartRate,
      "55283-6": QuestionnaireType.FetalHeartRate,
      "8716-3": QuestionnaireType.LaborVitals,
      labor_vitals: QuestionnaireType.LaborVitals,
      contractions: QuestionnaireType.Contractions,
      cervical_exam: QuestionnaireType.CervicalExam,
      "cervical-exam": QuestionnaireType.CervicalExam,
      rupture_of_membranes: QuestionnaireType.RuptureOfMembranes,
      rom: QuestionnaireType.RuptureOfMembranes,
      position: QuestionnaireType.Position,
      assessment_note: QuestionnaireType.AssessmentNote,
      "assessment-note": QuestionnaireType.AssessmentNote,
      birthing: QuestionnaireType.Birthing,
      inputOutput: QuestionnaireType.InputOutput,
      "input-output": QuestionnaireType.InputOutput,
      membranes: QuestionnaireType.Membranes,
      medsAndTherapies: QuestionnaireType.MedsAndTherapies,
      "meds-therapies": QuestionnaireType.MedsAndTherapies,
      newLocation: QuestionnaireType.NewLocation,
      location: QuestionnaireType.NewLocation,
      shoulderDystocia: QuestionnaireType.ShoulderDystocia,
      admitDischarge: QuestionnaireType.AdmitDischarge,
      admissionDischarge: QuestionnaireType.AdmitDischarge,
      estimatedBloodLoss: QuestionnaireType.EstimatedBloodLoss,
      transport: QuestionnaireType.Transport,
      resuscitation: QuestionnaireType.Resuscitation,
      postpartumHemorrhage: QuestionnaireType.PostpartumHemorrhage,
      // Add more mappings for any other form types that might appear
      fullLaborUpdate: QuestionnaireType.FullLaborUpdate,
    };
    return codeMap[code ?? ""];
  };

  // Get display name with date/time
  const getDisplayNameWithDateTime = (observation: Observation | undefined): string => {
    const code = observation?.code?.coding?.[0]?.code;
    const formName = normalizeObservationFormCode(code) ?? code;

    const questionnaireType = getQuestionnaireTypeFromCode(formName);
    const displayName = questionnaireType
      ? LABOR_QUESTIONNAIRE_DISPLAY_NAMES[questionnaireType]
      : title;

    const dateTime = observation?.effectiveDateTime
      ? longUSDateTimeFormat(observation.effectiveDateTime)
      : undefined;

    return dateTime ? `${displayName} - ${dateTime}` : displayName;
  };

  // Get the appropriate helper function based on observation type
  const getObservationValues = getLaborObservationDisplayValuesForVersionDiff;

  const currentValues = getObservationValues(currentObservation);
  const previousValues = getObservationValues(previousObservation);

  const codeForForm =
    currentObservation?.code?.coding?.[0]?.code ?? previousObservation?.code?.coding?.[0]?.code;
  const formNameForStructured = normalizeObservationFormCode(codeForForm);
  const {
    usesStructuredDiff: usesStructuredMedTherapyDiff,
    includeManeuvers: includeManeuversInStructuredDiff,
  } = getStructuredMedTherapyConfig(formNameForStructured);

  // Get all unique field names, excluding internal helper fields
  const excludeFields = ["hasAnswers", "hasNotes"];
  const structuredExcludeFields = usesStructuredMedTherapyDiff
    ? ["medications", "therapies", ...(includeManeuversInStructuredDiff ? ["maneuvers"] : [])]
    : [];

  const allFieldNames = new Set([
    ...Object.keys(currentValues).filter(
      (key) => !excludeFields.includes(key) && !structuredExcludeFields.includes(key),
    ),
    ...Object.keys(previousValues).filter(
      (key) => !excludeFields.includes(key) && !structuredExcludeFields.includes(key),
    ),
  ]);

  // Helper to format field names for display
  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  /** Flatten a plain object of display strings for diff text (avoids "[object Object]"). */
  const formatRecordValuesForDiff = (record: Record<string, unknown>): string => {
    return Object.values(record)
      .filter(
        (recordValue) => recordValue !== undefined && recordValue !== null && recordValue !== "",
      )
      .map((recordValue) => {
        if (Array.isArray(recordValue)) {
          return formatFieldValue(recordValue) ?? "";
        }
        if (
          typeof recordValue === "object" &&
          recordValue !== null &&
          "coding" in recordValue &&
          Array.isArray((recordValue as { coding?: unknown[] }).coding)
        ) {
          return getCodeableConceptCodingLabels(recordValue as CodeableConcept) ?? "";
        }
        if (
          typeof recordValue === "object" &&
          recordValue !== null &&
          !Array.isArray(recordValue)
        ) {
          return formatRecordValuesForDiff(recordValue as Record<string, unknown>);
        }
        return String(recordValue);
      })
      .filter(Boolean)
      .join(" · ");
  };

  const isCodingLike = (value: unknown): value is { code?: string; display?: unknown } =>
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    ("code" in value || "display" in value);

  // Helper to format field values for display
  const formatFieldValue = (value: unknown): string | undefined => {
    if (value === undefined || value === null || value === "") return undefined;
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) {
      if (value.length === 0) return undefined;
      const hasObjectElement = value.some(
        (item) => item !== null && typeof item === "object" && !Array.isArray(item),
      );
      if (hasObjectElement) {
        return value
          .map((item) => {
            if (item === undefined || item === null) return "";
            if (isCodingLike(item)) {
              return getCodingDisplayLabel(item as Coding);
            }
            if (typeof item === "object" && !Array.isArray(item)) {
              return formatRecordValuesForDiff(item as Record<string, unknown>);
            }
            return String(item);
          })
          .filter(Boolean)
          .join(", ");
      }
      return value.join(", ");
    }
    if (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value) &&
      "coding" in value &&
      Array.isArray((value as { coding?: unknown[] }).coding)
    ) {
      return getCodeableConceptCodingLabels(value as CodeableConcept);
    }
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      if (isCodingLike(value)) {
        const label = getCodingDisplayLabel(value as Coding);
        return label.length > 0 ? label : undefined;
      }
      return formatRecordValuesForDiff(value as Record<string, unknown>);
    }
    return String(value);
  };

  // Use the observation with date/time for the title
  const displayTitle = getDisplayNameWithDateTime(currentObservation || previousObservation);

  const structuredMedications = (currentValues as { medications?: LaborMedTherapyMedicationRow[] })
    .medications;
  const structuredPreviousMedications = (
    previousValues as { medications?: LaborMedTherapyMedicationRow[] }
  ).medications;
  const structuredTherapies = (currentValues as { therapies?: LaborMedTherapyTherapyRow[] })
    .therapies;
  const structuredPreviousTherapies = (
    previousValues as { therapies?: LaborMedTherapyTherapyRow[] }
  ).therapies;
  const structuredManeuvers = (currentValues as { maneuvers?: LaborMedTherapyManeuverRow[] })
    .maneuvers;
  const structuredPreviousManeuvers = (
    previousValues as { maneuvers?: LaborMedTherapyManeuverRow[] }
  ).maneuvers;

  const hasStructuredMedTherapyDiff =
    usesStructuredMedTherapyDiff &&
    (Boolean(structuredMedications?.length) ||
      Boolean(structuredPreviousMedications?.length) ||
      Boolean(structuredTherapies?.length) ||
      Boolean(structuredPreviousTherapies?.length) ||
      (includeManeuversInStructuredDiff &&
        (Boolean(structuredManeuvers?.length) || Boolean(structuredPreviousManeuvers?.length))));

  const hasPlainFieldDiff = laborObservationDisplayValuesDiffer(currentValues, previousValues, {
    structuredExcludeFields,
  });

  if (
    previousObservation &&
    currentObservation &&
    !hasStructuredMedTherapyDiff &&
    !hasPlainFieldDiff
  ) {
    return null;
  }

  return (
    <Paper p="md" mt="md" withBorder>
      <Stack mb="lg">
        <Title order={3}>{displayTitle}</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              {usesStructuredMedTherapyDiff && (
                <LaborObservationMedTherapySections
                  variant="previous"
                  currentManeuvers={structuredManeuvers}
                  currentTherapies={structuredTherapies ?? []}
                  previousManeuvers={structuredPreviousManeuvers}
                  currentMedications={structuredMedications ?? []}
                  includeManeuvers={includeManeuversInStructuredDiff}
                  previousTherapies={structuredPreviousTherapies ?? []}
                  previousMedications={structuredPreviousMedications ?? []}
                />
              )}
              {Array.from(allFieldNames).map((fieldName) => {
                const currentValue = formatFieldValue(
                  currentValues[fieldName as keyof typeof currentValues],
                );
                const previousValue = formatFieldValue(
                  previousValues[fieldName as keyof typeof previousValues],
                );

                return (
                  <Stack gap="0" key={fieldName}>
                    <TextWrapper fw={600}>{formatFieldName(fieldName)}</TextWrapper>
                    <WordsDiff
                      showRemoved
                      current={currentValue || ""}
                      previous={previousValue || ""}
                    />
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              {usesStructuredMedTherapyDiff && (
                <LaborObservationMedTherapySections
                  variant="amended"
                  currentManeuvers={structuredManeuvers}
                  currentTherapies={structuredTherapies ?? []}
                  previousManeuvers={structuredPreviousManeuvers}
                  currentMedications={structuredMedications ?? []}
                  includeManeuvers={includeManeuversInStructuredDiff}
                  previousTherapies={structuredPreviousTherapies ?? []}
                  previousMedications={structuredPreviousMedications ?? []}
                />
              )}
              {Array.from(allFieldNames).map((fieldName) => {
                const currentValue = formatFieldValue(
                  currentValues[fieldName as keyof typeof currentValues],
                );
                const previousValue = formatFieldValue(
                  previousValues[fieldName as keyof typeof previousValues],
                );

                return (
                  <Stack gap="0" key={fieldName}>
                    <TextWrapper fw={600}>{formatFieldName(fieldName)}</TextWrapper>
                    <WordsDiff current={currentValue || ""} previous={previousValue || ""} />
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default LaborObservationDiff;
